import type { TrustScoreTip } from '@carouselgg/auth/types'
import type { MyInvite } from '../../api/invites'
import type { MyTeamInvite } from '../../api/recruit'
import type { MyEntry } from '../../api/myActivity'
import { competitionPath } from './competitionsModel'
import { tipActionLabel } from '../trust/trustCopy'

/**
 * The Overview's "Needs you" list: what your account is waiting on, most
 * pressing first. An unverified email blocks sign-in and costs trust on every
 * game; an invite lapses; a trust tip only adds points. The list is short on
 * purpose, and each row says what to do and links straight to where you do it.
 */

export interface AccountTodo {
  id: string
  title: string
  detail: string
  /** No button when there's nothing to do on Carousel (ranked play happens in the game). */
  actionLabel?: string
  actionTo?: string
  /** Trust points it's worth, shown beside it. */
  points?: number
}

export interface AccountTodoInput {
  email: string
  emailVerified: boolean
  invites: MyInvite[]
  tips: TrustScoreTip[]
  /** Captains asking the player onto their team. */
  teamInvites?: MyTeamInvite[]
  /** Entries whose team still has to agree to the competition's rules. */
  rulesOwed?: MyEntry[]
  /** "now", injectable for tests. */
  now?: Date
}

/** More than this and the list stops being something you can act on. */
export const MAX_TODOS = 4

export function accountTodos({ email, emailVerified, invites, tips, teamInvites = [], rulesOwed = [], now = new Date() }: AccountTodoInput): AccountTodo[] {
  const out: AccountTodo[] = []

  if (!emailVerified) {
    out.push({
      id: 'email',
      title: 'Verify your email',
      detail: `We sent a link to ${email}. Until you click it, every trust score is lower.`,
      actionLabel: 'Resend link',
      actionTo: '/settings/sign-in#email',
    })
  }

  const live = invites
    .filter((i) => new Date(i.expires_at) > now)
    .sort((a, b) => a.expires_at.localeCompare(b.expires_at))
  for (const invite of live) {
    out.push({
      id: `invite-${invite.id}`,
      title: `${invite.organization.name} invited you to join as ${roleName(invite.role)}`,
      detail: `Answer by ${shortDate(invite.expires_at)} or it lapses.`,
      actionLabel: 'Review invite',
      actionTo: `/invite/${invite.token}`,
    })
  }

  // A team that hasn't agreed to the rules can't play under them.
  for (const e of rulesOwed) {
    out.push({
      id: `rules-${e.registration_id}`,
      title: `Agree to ${e.name}'s rules for ${e.team_name}`,
      detail: `Version ${e.rules_to_agree} is out. The league asks every captain to agree before they play.`,
      actionLabel: 'Read and agree',
      actionTo: competitionPath(e, 'rules'),
    })
  }

  for (const invite of teamInvites) {
    out.push({
      id: `team-invite-${invite.id}`,
      title: `${invite.team_name ?? 'A team'} want you on their roster`,
      detail: invite.expires_at ? `Answer by ${shortDate(invite.expires_at)} or it lapses.` : 'A captain is waiting on your answer.',
      actionLabel: 'Answer',
      actionTo: '/settings/competitions?tab=applications',
    })
  }

  for (const tip of tips) {
    // The email row above already covers it.
    if (tip.action === 'verify_email' && !emailVerified) continue
    // Some tips are done in the game, not here (play ranked, play this
    // month): the API gives them no link, so they get no button.
    out.push({
      id: `tip-${tip.category}-${tip.label}`,
      title: tip.label,
      detail: tip.action_url ? 'Raises your trust score.' : 'Counts automatically as you play on your linked accounts.',
      actionLabel: tip.action_url ? tipActionLabel(tip.action) : undefined,
      actionTo: tip.action_url || undefined,
      points: tip.points,
    })
  }

  return out.slice(0, MAX_TODOS)
}

function roleName(role: string): string {
  return role === 'admin' ? 'an admin' : role === 'owner' ? 'an owner' : `a ${role || 'member'}`
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
